import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar, Key, CreditCard, Clock, MapPin, Trash2, Copy, CheckCircle, CalendarDays, Tag, PartyPopper, Plus, Check, X as XIcon, ImagePlus, Globe, FileText, Download, Building2, Send, ChevronLeft, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { t, i18n } = useTranslation();
  const { user, setUser, token, loading: authLoading, updateLanguage } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const section = searchParams.get('section') || '';  // '', 'personal-info', 'billing', 'events'
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingLang, setSavingLang] = useState(false);

  const getHeaders = useCallback(() => {
    const h = {};
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, [token]);

  const fetchData = useCallback(async () => {
    try {
      const [bookingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/bookings`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/dashboard/stats`, { headers: getHeaders(), withCredentials: true })
      ]);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchData();
  }, [user, authLoading, navigate, fetchData]);

  // When the user lands here via the navbar dropdown (e.g. ?section=billing),
  // smoothly scroll the targeted section into view once the dashboard is rendered.
  useEffect(() => {
    if (!section || loading) return;
    const id = ({
      'personal-info': 'section-personal-info',
      'billing': 'section-billing',
      'events': 'section-events',
    })[section];
    if (!id) return;
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(t);
  }, [section, loading]);

  const cancelBooking = async (bookingId) => {
    if (!window.confirm(t('dashboard.cancelConfirm'))) return;
    try {
      const res = await axios.delete(`${API}/bookings/${bookingId}`, { headers: getHeaders(), withCredentials: true });
      if (res.data.refund_status === 'refunded') {
        toast.success(t('dashboard.cancelSuccessRefund'));
      } else {
        toast.success(t('dashboard.cancelSuccess'));
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('dashboard.cancelError'));
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(t('dashboard.codeCopied'));
  };

  const handleLanguageChange = async (lang) => {
    setSavingLang(true);
    try {
      await updateLanguage(lang);
      toast.success(t('dashboard.languagePrefSaved'));
    } catch {
      toast.error(t('dashboard.languagePrefError'));
    } finally {
      setSavingLang(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="w-8 h-8 border-2 border-[#1A3C34] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const activeBookings = bookings.filter(b => b.status === 'confirmed');
  const pendingBookings = bookings.filter(b => b.status === 'pending');
  const pastBookings = bookings.filter(b => b.status === 'cancelled' || b.status === 'expired');

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* When the user lands on a specific sub-section via the navbar dropdown
            (?section=personal-info|billing|events), we hide the whole "Mon
            tableau de bord" layout (header + stats + bookings tabs) and only
            render that section, preceded by a single "Retour au tableau de bord"
            link. This matches the simplification rule: one page = one purpose. */}
        {section && (
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            data-testid="section-back-btn"
            className="inline-flex items-center gap-1 text-xs text-[#1A3C34]/60 hover:text-[#1A3C34] transition-colors mb-4"
          >
            <ChevronLeft size={14} />
            {t('dashboard.backToOverview', 'Retour au tableau de bord')}
          </button>
        )}

        {!section && (<>
        {/* Header */}
        <div className="mb-8">
          <button
            type="button"
            onClick={() => navigate('/m')}
            data-testid="dashboard-back-btn"
            className="inline-flex items-center gap-1 text-xs text-[#1A3C34]/60 hover:text-[#1A3C34] transition-colors mb-3"
          >
            <ChevronLeft size={14} />
            {t('common.backToHub', "Retour à l'app")}
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="font-heading text-3xl font-semibold text-[#1A3C34]">
                {t('dashboard.title')}
              </h1>
              <p className="mt-1 text-sm text-[#737373]">
                {t('dashboard.welcome', { name: user?.name })}
              </p>
            </div>
            <Button
              data-testid="new-booking-btn-top"
              onClick={() => navigate('/plan')}
              className="bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90 rounded-md h-11 px-6 self-start sm:self-auto"
            >
              <MapPin className="w-4 h-4 mr-2" />
              {t('dashboard.newBookingBtn')}
            </Button>
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-[#E2E2DF] rounded-md p-5" data-testid="stat-total-bookings">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-[#F0EFEA] flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#1A3C34]" />
              </div>
              <div>
                <div className="font-mono text-2xl font-semibold text-[#1A3C34]">
                  {stats?.total_bookings || 0}
                </div>
                <div className="text-xs text-[#737373]">{t('dashboard.totalBookings')}</div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#E2E2DF] rounded-md p-5" data-testid="stat-active-bookings">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-green-50 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="font-mono text-2xl font-semibold text-[#1A3C34]">
                  {stats?.active_bookings || 0}
                </div>
                <div className="text-xs text-[#737373]">{t('dashboard.activeBookings')}</div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#E2E2DF] rounded-md p-5" data-testid="stat-subscription">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-[#C77D63]/10 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-[#C77D63]" />
              </div>
              <div>
                <div className="text-sm font-semibold text-[#1A3C34]">
                  {stats?.subscription?.status === 'active' ? t(`subscriptions.planNames.${stats.subscription.plan_id}`, { defaultValue: stats.subscription.plan_name }) : t('dashboard.none')}
                </div>
                <div className="text-xs text-[#737373]">{t('dashboard.subscription')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription banner */}
        {stats?.subscription?.status === 'active' && (
          <div className="mb-8 bg-gradient-to-r from-[#1A3C34] to-[#2a5c4e] rounded-md p-5" data-testid="subscription-banner">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Tag className="w-5 h-5 text-[#C77D63]" />
                </div>
                <div>
                  <div className="text-white font-semibold text-sm">{t(`subscriptions.planNames.${stats.subscription.plan_id}`, { defaultValue: stats.subscription.plan_name })}</div>
                  <div className="text-white/60 text-xs">
                    -{stats.subscription.discount_percent}% {t('dashboard.discountAll')}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5 text-white/80">
                  <CalendarDays className="w-3.5 h-3.5" />
                  <span>{t('dashboard.starts')} : <strong className="text-white">{(() => {
                    try {
                      const locale = i18n.language?.startsWith('nl') ? 'nl-BE' : i18n.language?.startsWith('en') ? 'en-US' : 'fr-BE';
                      return new Date(stats.subscription.starts_at).toLocaleDateString(locale);
                    } catch { return stats.subscription.starts_at?.slice(0,10) || ''; }
                  })()}</strong></span>
                </div>
                <div className="flex items-center gap-1.5 text-white/80">
                  <CalendarDays className="w-3.5 h-3.5" />
                  <span>{t('dashboard.ends')} : <strong className="text-white">{(() => {
                    try {
                      const locale = i18n.language?.startsWith('nl') ? 'nl-BE' : i18n.language?.startsWith('en') ? 'en-US' : 'fr-BE';
                      return new Date(stats.subscription.expires_at).toLocaleDateString(locale);
                    } catch { return stats.subscription.expires_at?.slice(0,10) || ''; }
                  })()}</strong></span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bookings tabs */}
        <Tabs defaultValue="active" className="space-y-4" data-testid="bookings-tabs">
          <TabsList className="bg-white border border-[#E2E2DF]">
            <TabsTrigger value="active" data-testid="tab-active">
              {t('dashboard.tabActive')} ({activeBookings.length})
            </TabsTrigger>
            <TabsTrigger value="pending" data-testid="tab-pending">
              {t('dashboard.tabPending')} ({pendingBookings.length})
            </TabsTrigger>
            <TabsTrigger value="past" data-testid="tab-past">
              {t('dashboard.tabPast')} ({pastBookings.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            <BookingsList bookings={activeBookings} onCancel={cancelBooking} copyCode={copyCode} getHeaders={getHeaders} onRefresh={fetchData} />
          </TabsContent>
          <TabsContent value="pending">
            <BookingsList bookings={pendingBookings} onCancel={cancelBooking} copyCode={copyCode} getHeaders={getHeaders} onRefresh={fetchData} />
          </TabsContent>
          <TabsContent value="past">
            <BookingsList bookings={pastBookings} isPast />
          </TabsContent>
        </Tabs>
        </>)}

        {/* My Events — only shown when accessed via the navbar dropdown */}
        {section === 'events' && (
          <div id="section-events">
            <MyEventsSection token={token} getHeaders={getHeaders} />
          </div>
        )}

        {/* Personal info (name / phone / email / password / language / billing) — opened from the navbar dropdown */}
        {section === 'personal-info' && (
          <div id="section-personal-info">
            <PersonalInfoSection
              getHeaders={getHeaders}
              user={user}
              setUser={setUser}
              currentLang={user?.preferred_language || i18n.language?.slice(0, 2) || 'fr'}
              onLanguageChange={handleLanguageChange}
              savingLang={savingLang}
            />
            <BillingProfileSection token={token} getHeaders={getHeaders} user={user} setUser={setUser} />
          </div>
        )}

        {/* My invoices list — opened from the navbar dropdown (billing profile is now merged into Mes données) */}
        {section === 'billing' && (
          <div id="section-billing">
            <MyInvoicesSection token={token} getHeaders={getHeaders} />
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// DOOR HOLD-OPEN CONTROLS — toggle the "porte ouverte" preference and,
// during the active session window, fire the live lock-open / lock-close
// commands on the Pi.
// ============================================================
function DoorHoldOpenControls({ booking, getHeaders, onRefresh, t }) {
  const [holdOpen, setHoldOpen] = useState(Boolean(booking?.door_hold_open));
  const [savingPref, setSavingPref] = useState(false);
  const [livePending, setLivePending] = useState(null); // 'open' | 'close' | null

  useEffect(() => {
    setHoldOpen(Boolean(booking?.door_hold_open));
  }, [booking?.door_hold_open]);

  // Are we within the booking's time window right now? (Brussels time roughly = local browser tz for the user)
  const isLive = (() => {
    try {
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      if (booking.date !== today) return false;
      const [sh, sm] = (booking.start_time || '00:00').split(':').map(Number);
      const [eh, em] = (booking.end_time || '23:59').split(':').map(Number);
      const start = sh * 60 + (sm || 0) - 15; // 15min grace before
      const end = eh * 60 + (em || 0) + 15;   // 15min grace after
      const cur = now.getHours() * 60 + now.getMinutes();
      return cur >= start && cur <= end;
    } catch { return false; }
  })();

  const togglePref = async (next) => {
    setSavingPref(true);
    try {
      await axios.put(
        `${API}/bookings/${booking.booking_id}/door-hold`,
        { door_hold_open: next },
        { headers: getHeaders(), withCredentials: true },
      );
      setHoldOpen(next);
      toast.success(
        next
          ? t('dashboard.doorHoldOnSaved', { defaultValue: 'Mode "porte ouverte" activé pour cette séance' })
          : t('dashboard.doorHoldOffSaved', { defaultValue: 'Mode "porte ouverte" désactivé' })
      );
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la mise à jour');
    } finally {
      setSavingPref(false);
    }
  };

  const fireLive = async (action) => {
    setLivePending(action);
    try {
      const url = action === 'open'
        ? `${API}/access/user/lock-open`
        : `${API}/access/user/lock-close`;
      const res = await axios.post(
        url,
        { booking_id: booking.booking_id },
        { headers: getHeaders(), withCredentials: true },
      );
      if (res.data?.status === 'acked') {
        toast.success(action === 'open'
          ? t('dashboard.doorOpenedLive', { defaultValue: 'Porte verrouillée ouverte ✓' })
          : t('dashboard.doorClosedLive', { defaultValue: 'Porte refermée (mode normal) ✓' }));
      } else if (res.data?.status === 'timeout') {
        toast.warning(t('dashboard.doorLiveTimeout', { defaultValue: 'Le boîtier est hors ligne. La commande passera dès qu\'il revient (max 60 sec).' }));
      } else {
        toast.error(t('dashboard.doorLiveFailed', { defaultValue: 'Échec — voir avec l\'admin.' }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur réseau');
    } finally {
      setLivePending(null);
    }
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid={`door-hold-controls-${booking.booking_id}`}>
      <label className="flex items-center gap-1.5 cursor-pointer text-[#737373]">
        <input
          type="checkbox"
          checked={holdOpen}
          onChange={(e) => togglePref(e.target.checked)}
          disabled={savingPref}
          className="w-3.5 h-3.5 accent-[#1A3C34]"
          data-testid={`door-hold-toggle-${booking.booking_id}`}
        />
        <span className="select-none">
          🚪 {t('dashboard.doorHoldOpenLabel', { defaultValue: 'Mode conf (porte ouverte session)' })}
        </span>
      </label>
      {holdOpen && isLive && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] font-semibold uppercase">
          ● {t('dashboard.doorLiveBadge', { defaultValue: 'séance en cours' })}
        </span>
      )}
      {holdOpen && isLive && (
        <>
          <Button
            type="button"
            size="sm"
            onClick={() => fireLive('open')}
            disabled={livePending !== null}
            className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] px-2"
            data-testid={`door-live-open-${booking.booking_id}`}
          >
            {livePending === 'open' ? '…' : t('dashboard.doorOpenNow', { defaultValue: '🚪 Ouvrir maintenant' })}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => fireLive('close')}
            disabled={livePending !== null}
            className="h-7 text-[11px] px-2 border-[#E2E2DF]"
            data-testid={`door-live-close-${booking.booking_id}`}
          >
            {livePending === 'close' ? '…' : t('dashboard.doorCloseNow', { defaultValue: 'Refermer' })}
          </Button>
        </>
      )}
      {holdOpen && !isLive && (
        <span className="text-[10px] text-[#737373] italic">
          {t('dashboard.doorHoldHintLater', { defaultValue: 'Les boutons "ouvrir/fermer" apparaîtront le jour de la séance.' })}
        </span>
      )}
    </div>
  );
}


function BookingsList({ bookings, onCancel, copyCode, isPast, getHeaders, onRefresh }) {
  const { t } = useTranslation();
  if (bookings.length === 0) {
    return (
      <div className="bg-white border border-dashed border-[#E2E2DF] rounded-md p-12 text-center">
        <Calendar className="w-8 h-8 text-[#E2E2DF] mx-auto mb-3" />
        <p className="text-sm text-[#737373]">{t('dashboard.noBookingsCard')}</p>
      </div>
    );
  }

  // Group multi-day series sharing a recurring_id (or, as a fallback, the same
  // access_code + space_id + time slot). Each group is rendered as a SINGLE
  // card showing the date range "du DD/MM au DD/MM". Single-day bookings stay
  // as one-element groups → identical rendering path.
  const groups = [];
  const seen = new Set();
  for (const b of bookings) {
    if (seen.has(b.booking_id)) continue;
    let siblings;
    if (b.recurring_id) {
      siblings = bookings.filter((x) => x.recurring_id === b.recurring_id);
    } else if (b.access_code) {
      siblings = bookings.filter(
        (x) =>
          !x.recurring_id &&
          x.access_code === b.access_code &&
          x.space_id === b.space_id &&
          x.start_time === b.start_time &&
          x.end_time === b.end_time &&
          x.status === b.status,
      );
    } else {
      siblings = [b];
    }
    // Sort by date asc so range boundaries are correct
    siblings.sort((a, c) => (a.date || '').localeCompare(c.date || ''));
    siblings.forEach((s) => seen.add(s.booking_id));
    groups.push(siblings);
  }

  return (
    <div className="space-y-3">
      {groups.map((group) => {
        const head = group[0];
        const tail = group[group.length - 1];
        const isSeries = group.length > 1;
        const totalPrice = group.reduce((s, x) => s + (Number(x.total_price) || 0), 0);
        return (
        <div
          key={head.booking_id}
          className="bg-white border border-[#E2E2DF] rounded-md p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
          data-testid={`booking-card-${head.booking_id}`}
        >
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="font-body text-sm font-semibold text-[#1A3C34]">{head.space_name || t(`spaces.names.${head.space_id}`, { defaultValue: head.space_id })}</span>
              <Badge
                className={`text-xs ${
                  head.status === 'confirmed'
                    ? 'bg-green-100 text-green-700'
                    : head.status === 'pending'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {head.status === 'confirmed' ? t('dashboard.statusConfirmed') : head.status === 'pending' ? t('dashboard.statusPending') : t('dashboard.statusCancelled')}
              </Badge>
              {isSeries && (
                <Badge className="bg-[#C77D63]/10 text-[#C77D63] text-[10px]" data-testid={`booking-series-badge-${head.booking_id}`}>
                  {group.length} {t('dashboard.days', { defaultValue: 'jours' })}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs text-[#737373] flex-wrap">
              <span className="flex items-center gap-1" data-testid={`booking-dates-${head.booking_id}`}>
                <Calendar className="w-3 h-3" />
                {isSeries ? (
                  <>{t('dashboard.fromTo', { start: head.date, end: tail.date })}</>
                ) : head.booking_type === 'weekly' ? (
                  <>{t('dashboard.fromTo', { start: head.date, end: (() => {
                    try {
                      const d = new Date(head.date + 'T00:00:00');
                      d.setDate(d.getDate() + 6);
                      return d.toISOString().split('T')[0];
                    } catch { return ''; }
                  })() })}</>
                ) : head.date}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> {head.start_time} - {head.end_time}
              </span>
              {head.booking_type === 'weekly' && (
                <Badge className="bg-[#C77D63]/10 text-[#C77D63] text-[10px]">{t('dashboard.weekly')}</Badge>
              )}
              {head.booking_type === 'daily' && (
                <Badge className="bg-[#4A90D9]/10 text-[#4A90D9] text-[10px]">{t('dashboard.daily')}</Badge>
              )}
              <span className="font-mono">{totalPrice.toFixed(2)} EUR{isSeries ? ` (${group.length}×)` : ''}</span>
            </div>
            {/* Door hold-open controls (only meaningful for confirmed future/active bookings). For a series
                we surface the control on the FIRST occurrence (representative of the whole series). */}
            {!isPast && head.status === 'confirmed' && getHeaders && (
              <DoorHoldOpenControls booking={head} getHeaders={getHeaders} onRefresh={onRefresh} t={t} />
            )}
          </div>
          <div className="flex items-center gap-2">
            {head.access_code && (
              <button
                data-testid={`booking-code-${head.booking_id}`}
                onClick={() => copyCode(head.access_code)}
                className="flex items-center gap-1 px-3 py-1.5 bg-[#1A3C34] text-white rounded-md text-xs font-mono hover:bg-[#1A3C34]/90 transition-colors"
              >
                <Key className="w-3 h-3" />
                {head.access_code}
              </button>
            )}
            {!isPast && (head.status === 'pending' || head.status === 'confirmed') && (
              <Button
                data-testid={`cancel-booking-${head.booking_id}`}
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (isSeries) {
                    if (!window.confirm(t('dashboard.cancelSeriesConfirm', {
                      defaultValue: `Annuler la série de ${group.length} réservations ?`,
                      count: group.length,
                    }))) return;
                    group.forEach((x) => onCancel(x.booking_id));
                  } else {
                    onCancel(head.booking_id);
                  }
                }}
                className="text-red-500 hover:text-red-600 hover:bg-red-50 text-xs"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                {t('dashboard.cancel')}
              </Button>
            )}
          </div>
        </div>
        );
      })}
    </div>
  );
}


const STATUS_LABELS = {
  pending: { text: 'En attente de validation', cls: 'bg-yellow-100 text-yellow-700' },
  published: { text: 'Publie', cls: 'bg-green-100 text-green-700' },
  rejected: { text: 'Refuse', cls: 'bg-red-100 text-red-600' },
};

function MyEventsSection({ token, getHeaders }) {
  const { t } = useTranslation();
  const STATUS_LABELS_LOCAL = {
    pending: { text: t('dashboard.eventStatusPending'), cls: 'bg-yellow-100 text-yellow-700' },
    published: { text: t('dashboard.eventStatusPublished'), cls: 'bg-green-100 text-green-700' },
    rejected: { text: t('dashboard.eventStatusRejected'), cls: 'bg-red-100 text-red-600' },
  };
  const [myEvents, setMyEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);
  const fileRef = React.useRef(null);
  const [form, setForm] = useState({
    title: '', description: '', event_type: 'event', date: '',
    start_time: '09:00', end_time: '17:00', organizer: '',
    location: 'Flexi-location, 304 Chaussee de Stockel',
    price: '', max_participants: '', contact_email: '', contact_phone: '', facebook_url: '', tags: '',
    image_url: ''
  });

  const fetchMyEvents = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/events/my`, {
        headers: getHeaders(), withCredentials: true
      });
      setMyEvents(res.data);
    } catch { /* ignore */ }
  }, [token, getHeaders]);

  useEffect(() => { fetchMyEvents(); }, [fetchMyEvents]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      setForm(f => ({ ...f, image_url: res.data.image_url }));
      toast.success(t('dashboard.imageUploaded'));
    } catch {
      toast.error(t('dashboard.imageUploadError'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!form.title || !form.date) {
      toast.error(t('dashboard.titleAndDateRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        price: form.price ? parseFloat(form.price) : 0,
        max_participants: form.max_participants ? parseInt(form.max_participants) : null,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : []
      };
      await axios.post(`${API}/events/submit`, payload, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' }, withCredentials: true
      });
      toast.success(t('dashboard.eventSubmitted'));
      setShowForm(false);
      setForm({ title: '', description: '', event_type: 'event', date: '', start_time: '09:00', end_time: '17:00', organizer: '', location: 'Flexi-location, 304 Chaussee de Stockel', price: '', max_participants: '', contact_email: '', contact_phone: '', facebook_url: '', tags: '', image_url: '' });
      fetchMyEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('dashboard.genericError'));
    } finally { setSubmitting(false); }
  };

  const handleToggleFull = async (eventId) => {
    try {
      await axios.put(`${API}/events/${eventId}/toggle-full`, {}, {
        headers: getHeaders(), withCredentials: true
      });
      fetchMyEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('dashboard.genericError'));
    }
  };

  return (
    <div className="mt-8" data-testid="my-events-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-semibold text-[#1A3C34] flex items-center gap-2">
          <PartyPopper className="w-5 h-5 text-[#C77D63]" />
          {t('dashboard.myEventsHeader')}
        </h2>
        <Button
          size="sm"
          onClick={() => setShowForm(!showForm)}
          className="bg-[#C77D63] text-white hover:bg-[#C77D63]/90 h-8 text-xs"
          data-testid="submit-event-btn"
        >
          <Plus className="w-3 h-3 mr-1" /> {t('dashboard.submitEventBtn')}
        </Button>
      </div>

      {/* Submission form */}
      {showForm && (
        <div className="bg-white border border-[#C77D63]/30 rounded-md p-4 mb-4 space-y-3" data-testid="user-event-form">
          <div className="flex items-center gap-2 mb-1">
            <PartyPopper className="w-4 h-4 text-[#C77D63]" />
            <h4 className="text-sm font-semibold text-[#1A3C34]">{t('dashboard.submitEventTitle')}</h4>
          </div>
          <p className="text-[11px] text-[#737373]">{t('dashboard.submitEventDesc')}</p>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Image upload */}
            <div className="sm:col-span-2">
              <label className="text-xs text-[#737373]">{t('dashboard.photoLabel')}</label>
              {form.image_url ? (
                <div className="mt-1 flex items-start gap-3">
                  <img
                    src={form.image_url}
                    alt="preview"
                    className="w-24 h-24 object-cover rounded-md border border-[#E2E2DF] cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setLightboxImg(form.image_url)}
                  />
                  <div className="flex flex-col gap-1">
                    <Button size="sm" variant="outline" className="h-7 text-[10px] border-[#E2E2DF]" onClick={() => fileRef.current?.click()} disabled={uploading}>
                      <ImagePlus className="w-3 h-3 mr-1" /> {t('dashboard.change')}
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-[10px] border-red-200 text-red-500 hover:bg-red-50" onClick={() => setForm(f => ({...f, image_url: ''}))}>
                      <Trash2 className="w-3 h-3 mr-1" /> {t('dashboard.remove')}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  size="sm" variant="outline"
                  className="w-full h-16 mt-1 border-dashed border-[#E2E2DF] text-[#737373] hover:border-[#C77D63] hover:text-[#C77D63]"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                >
                  <ImagePlus className="w-5 h-5 mr-2" />
                  {uploading ? t('dashboard.uploading') : t('dashboard.addPhoto')}
                </Button>
              )}
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-[#737373]">{t('dashboard.titleRequired')}</label>
              <Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" placeholder={t('dashboard.titlePlaceholder')} />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-[#737373]">{t('dashboard.descLabel')}</label>
              <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                className="w-full mt-1 p-2 text-sm border border-[#E2E2DF] rounded-md resize-none h-16" placeholder={t('dashboard.descPlaceholder')} />
            </div>
            <div>
              <label className="text-xs text-[#737373]">{t('dashboard.typeLabel')}</label>
              <Select value={form.event_type} onValueChange={v => setForm(f => ({...f, event_type: v}))}>
                <SelectTrigger className="h-8 text-sm border-[#E2E2DF] mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="event">{t('dashboard.typeEvent')}</SelectItem>
                  <SelectItem value="formation">{t('dashboard.typeFormation')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#737373]">{t('dashboard.dateRequired')}</label>
              <Input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" />
            </div>
            <div>
              <label className="text-xs text-[#737373]">{t('dashboard.startHour')}</label>
              <Input type="time" value={form.start_time} onChange={e => setForm(f => ({...f, start_time: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" />
            </div>
            <div>
              <label className="text-xs text-[#737373]">{t('dashboard.endHour')}</label>
              <Input type="time" value={form.end_time} onChange={e => setForm(f => ({...f, end_time: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" />
            </div>
            <div>
              <label className="text-xs text-[#737373]">{t('dashboard.organizer')}</label>
              <Input value={form.organizer} onChange={e => setForm(f => ({...f, organizer: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" placeholder={t('dashboard.organizerPlaceholder')} />
            </div>
            <div>
              <label className="text-xs text-[#737373]">{t('dashboard.priceLabel')}</label>
              <Input type="number" value={form.price} onChange={e => setForm(f => ({...f, price: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" />
            </div>
            <div>
              <label className="text-xs text-[#737373]">{t('dashboard.contactEmail')}</label>
              <Input value={form.contact_email} onChange={e => setForm(f => ({...f, contact_email: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" />
            </div>
            <div>
              <label className="text-xs text-[#737373]">{t('dashboard.contactPhone', { defaultValue: 'Téléphone' })}</label>
              <Input value={form.contact_phone} onChange={e => setForm(f => ({...f, contact_phone: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" placeholder="+32 …" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-[#737373]">{t('dashboard.facebookUrl', { defaultValue: 'Lien Facebook (événement / page)' })}</label>
              <Input value={form.facebook_url} onChange={e => setForm(f => ({...f, facebook_url: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" placeholder="https://www.facebook.com/events/…" />
            </div>
            <div>
              <label className="text-xs text-[#737373]">{t('dashboard.tagsLabel')}</label>
              <Input value={form.tags} onChange={e => setForm(f => ({...f, tags: e.target.value}))} className="h-8 text-sm border-[#E2E2DF] mt-1" placeholder={t('dashboard.tagsPlaceholder')} />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button size="sm" onClick={handleSubmit} disabled={submitting} className="bg-[#C77D63] text-white hover:bg-[#C77D63]/90 h-8 text-xs">
              <Check className="w-3 h-3 mr-1" /> {submitting ? t('dashboard.sending') : t('dashboard.submitForValidation')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)} className="h-8 text-xs border-[#E2E2DF]">
              {t('dashboard.cancel')}
            </Button>
          </div>
        </div>
      )}

      {/* My events list */}
      {myEvents.length > 0 && (
        <div className="space-y-2">
          {myEvents.map(evt => {
            const st = STATUS_LABELS_LOCAL[evt.status] || STATUS_LABELS_LOCAL.pending;
            return (
              <div key={evt.event_id} className="bg-white border border-[#E2E2DF] rounded-md p-3 flex items-center gap-3" data-testid={`my-event-${evt.event_id}`}>
                {evt.image_url && (
                  <img
                    src={evt.image_url}
                    alt={evt.title}
                    className="w-14 h-14 object-cover rounded-md flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity border border-[#E2E2DF]"
                    onClick={() => setLightboxImg(evt.image_url)}
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-[#1A3C34]">{evt.title}</span>
                    <Badge className={`text-[10px] ${st.cls}`}>{st.text}</Badge>
                    <Badge className={`text-[10px] ${evt.event_type === 'formation' ? 'bg-[#4A90D9]/10 text-[#4A90D9]' : 'bg-[#C77D63]/10 text-[#C77D63]'}`}>
                      {evt.event_type === 'formation' ? t('dashboard.typeFormation') : t('dashboard.typeEvent')}
                    </Badge>
                    {evt.is_full && <Badge className="bg-red-600 text-white text-[10px] font-bold">{t('dashboard.fullBadge')}</Badge>}
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-[#737373] mt-1">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {evt.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {evt.start_time}-{evt.end_time}</span>
                  </div>
                </div>
                {evt.status === 'published' && (
                  <label className="flex items-center gap-1.5 cursor-pointer flex-shrink-0" title={t('dashboard.markFullTitle')}>
                    <input
                      type="checkbox"
                      checked={!!evt.is_full}
                      onChange={() => handleToggleFull(evt.event_id)}
                      className="w-3.5 h-3.5 rounded border-[#E2E2DF] text-red-600 focus:ring-red-500 cursor-pointer"
                    />
                    <span className="text-[10px] text-[#737373]">{t('dashboard.fullLabel')}</span>
                  </label>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {lightboxImg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={() => setLightboxImg(null)}>
          <div className="relative max-w-3xl max-h-[85vh]">
            <img src={lightboxImg} alt="Agrandir" className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" />
            <button onClick={() => setLightboxImg(null)} className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-[#737373] hover:text-[#1A3C34]">
              <XIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================
// MY INVOICES SECTION
// ============================================================
function MyInvoicesSection({ token, getHeaders }) {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/invoices`, { headers: getHeaders(), withCredentials: true });
        if (!cancelled) setInvoices(res.data || []);
      } catch (e) {
        console.error('Invoices fetch error:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getHeaders]);

  const downloadInvoice = async (invoiceNumber) => {
    try {
      const res = await axios.get(`${API}/invoices/${invoiceNumber}/pdf`, {
        headers: getHeaders(),
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed', e);
      toast.error(t('invoices.downloadError', { defaultValue: 'Téléchargement impossible' }));
    }
  };

  const downloadInvoiceXml = async (invoiceNumber) => {
    try {
      const res = await axios.get(`${API}/invoices/${invoiceNumber}/xml`, {
        headers: getHeaders(),
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('XML Peppol téléchargé');
    } catch (e) {
      toast.error('XML indisponible pour ce document');
    }
  };

  if (loading) return null;
  if (!invoices.length) return null;

  // Invoice type derivation for the badge :
  //   • 'business+peppol' → pro_vat entreprise Peppol (PDF + XML)
  //   • 'business'        → pro_non_vat (facture B2B avec BCE, sans Peppol)
  //   • 'private'         → particulier (facture privée, non déductible TVA)
  // We rely on `invoice_type` (the client's checkout choice), `peppol_enabled`
  // (only when pro_vat + complete profile), and the customer snapshot's `bce`
  // to distinguish non-assujetti from a pure private receipt.
  const kindOf = (inv) => {
    if (inv.peppol_enabled) return 'business_peppol';
    if ((inv.invoice_type || 'private') === 'business') return 'business';
    if ((inv.customer?.bce || '').trim()) return 'business'; // resynced non-vat
    return 'private';
  };
  const peppolCount = invoices.filter((i) => kindOf(i) === 'business_peppol').length;
  const businessCount = invoices.filter((i) => kindOf(i) === 'business').length;
  const privateCount = invoices.filter((i) => kindOf(i) === 'private').length;

  return (
    <div className="mt-10" data-testid="my-invoices-section">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <FileText className="w-5 h-5 text-[#1A3C34]" />
        <h2 className="text-xl font-semibold text-[#1A3C34]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('invoices.myInvoicesTitle', { defaultValue: 'Mes factures' })}
        </h2>
        {peppolCount > 0 && (
          <Badge variant="outline" className="ml-1 bg-[#C77D63]/10 border-[#C77D63]/30 text-[#C77D63]">
            {peppolCount} entreprise (Peppol)
          </Badge>
        )}
        {businessCount > 0 && (
          <Badge variant="outline" className="bg-[#A0826D]/10 border-[#A0826D]/40 text-[#A0826D]">
            {businessCount} pro (non assujetti)
          </Badge>
        )}
        {privateCount > 0 && (
          <Badge variant="outline" className="bg-[#1A3C34]/5 border-[#1A3C34]/30 text-[#1A3C34]">
            {privateCount} privée{privateCount > 1 ? 's' : ''}
          </Badge>
        )}
      </div>
      <div className="bg-white border border-[#E2E2DF] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="bg-[#F9F9F7] text-[#737373]">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Type</th>
                <th className="text-left px-4 py-3 font-medium">{t('invoices.number', { defaultValue: 'Numéro' })}</th>
                <th className="text-left px-4 py-3 font-medium">{t('invoices.date', { defaultValue: 'Date' })}</th>
                <th className="text-left px-4 py-3 font-medium">{t('invoices.amount', { defaultValue: 'Montant' })}</th>
                <th className="text-left px-4 py-3 font-medium">{t('invoices.payment', { defaultValue: 'Paiement' })}</th>
                <th className="text-right px-4 py-3 font-medium">{t('invoices.action', { defaultValue: 'Action' })}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const kind = kindOf(inv);
                const isPeppol = kind === 'business_peppol';
                return (
                <tr key={inv.invoice_number} className="border-t border-[#E2E2DF]" data-testid={`invoice-row-${inv.invoice_number}`}>
                  <td className="px-4 py-3">
                    {kind === 'business_peppol' && (
                      <Badge className="bg-[#C77D63]/10 text-[#C77D63] border border-[#C77D63]/30" data-testid={`kind-badge-business-${inv.invoice_number}`}>
                        Entreprise (Peppol)
                      </Badge>
                    )}
                    {kind === 'business' && (
                      <Badge className="bg-[#A0826D]/10 text-[#A0826D] border border-[#A0826D]/40" data-testid={`kind-badge-pronovat-${inv.invoice_number}`}>
                        Pro (non assujetti)
                      </Badge>
                    )}
                    {kind === 'private' && (
                      <Badge className="bg-[#1A3C34]/10 text-[#1A3C34] border border-[#1A3C34]/30" data-testid={`kind-badge-private-${inv.invoice_number}`}>
                        Privée
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-[#1A3C34]">{inv.invoice_number}</td>
                  <td className="px-4 py-3 text-[#1A3C34]">{inv.issued_at_display}</td>
                  <td className="px-4 py-3 text-[#1A3C34] font-medium">{(inv.totals?.tvac ?? 0).toFixed(2)} € TVAC</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="capitalize">{inv.payment_method || '—'}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1 justify-end flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadInvoice(inv.invoice_number)}
                        data-testid={`download-invoice-${inv.invoice_number}`}
                      >
                        <Download className="w-3.5 h-3.5 mr-1" />
                        PDF
                      </Button>
                      {isPeppol && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadInvoiceXml(inv.invoice_number)}
                          className="border-[#C77D63]/40 text-[#C77D63] hover:bg-[#C77D63]/10"
                          title="Peppol UBL 2.1 XML — pour votre comptable ou intégration directe"
                          data-testid={`download-invoice-xml-${inv.invoice_number}`}
                        >
                          <Download className="w-3.5 h-3.5 mr-1" />
                          XML Peppol
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PERSONAL INFO SECTION (name / phone / email / password)
// ============================================================
function PersonalInfoSection({ getHeaders, user, setUser, currentLang, onLanguageChange, savingLang }) {
  const { t } = useTranslation();
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [email, setEmail] = useState(user?.email || '');
  const [savingProfile, setSavingProfile] = useState(false);

  const [showPwd, setShowPwd] = useState(false);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [newPwd2, setNewPwd2] = useState('');
  const [savingPwd, setSavingPwd] = useState(false);

  const [showDelete, setShowDelete] = useState(false);
  const [deletePwd, setDeletePwd] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  const isGoogleOnly = user?.auth_provider === 'google';

  useEffect(() => {
    setName(user?.name || '');
    setPhone(user?.phone || '');
    setEmail(user?.email || '');
  }, [user?.name, user?.phone, user?.email]);

  const dirty =
    (name || '').trim() !== (user?.name || '').trim() ||
    (phone || '').trim() !== (user?.phone || '').trim() ||
    (email || '').trim().toLowerCase() !== (user?.email || '').trim().toLowerCase();

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const res = await axios.put(
        `${API}/auth/profile`,
        { name: name.trim(), phone: phone.trim(), email: email.trim().toLowerCase() },
        { headers: getHeaders(), withCredentials: true },
      );
      if (res.data?.updated) {
        if (setUser) setUser((u) => ({ ...(u || {}), ...(res.data || {}) }));
        toast.success(t('profile.saved', { defaultValue: 'Informations enregistrées' }));
      } else {
        toast.info('Aucun changement');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async () => {
    if (!currentPwd || !newPwd) {
      toast.error('Mot de passe actuel et nouveau requis');
      return;
    }
    if (newPwd.length < 8) {
      toast.error('Nouveau mot de passe : minimum 8 caractères');
      return;
    }
    if (newPwd !== newPwd2) {
      toast.error('La confirmation ne correspond pas');
      return;
    }
    setSavingPwd(true);
    try {
      await axios.put(
        `${API}/auth/password`,
        { current_password: currentPwd, new_password: newPwd },
        { headers: getHeaders(), withCredentials: true },
      );
      toast.success('Mot de passe modifié ✓');
      setCurrentPwd('');
      setNewPwd('');
      setNewPwd2('');
      setShowPwd(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec changement mot de passe');
    } finally {
      setSavingPwd(false);
    }
  };

  const deleteAccount = async () => {
    if ((deleteConfirm || '').trim().toUpperCase() !== 'SUPPRIMER') {
      toast.error("Tapez SUPPRIMER pour confirmer");
      return;
    }
    if (!isGoogleOnly && !deletePwd) {
      toast.error("Mot de passe requis");
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/account`, {
        headers: getHeaders(),
        withCredentials: true,
        data: { password: deletePwd, confirm: deleteConfirm.trim().toUpperCase() },
      });
      toast.success('Compte supprimé. À bientôt.');
      // Clear local auth + redirect to home
      try { localStorage.removeItem('auth_token'); } catch {}
      window.location.href = '/';
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec suppression');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mt-10" data-testid="personal-info-section">
      <div className="flex items-center gap-2 mb-4">
        <User className="w-5 h-5 text-[#1A3C34]" />
        <h2 className="text-xl font-semibold text-[#1A3C34]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('nav.myData', 'Mes données')}
        </h2>
      </div>

      <div className="bg-white border border-[#E2E2DF] rounded-lg p-5 space-y-5">
          {/* Language preference — moved into "Mes données" for clarity (was a standalone card before) */}
          {onLanguageChange && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-[#E2E2DF]">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-md bg-[#F0EFEA] flex items-center justify-center flex-shrink-0">
                  <Globe className="w-4 h-4 text-[#1A3C34]" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#1A3C34]">
                    {t('dashboard.languagePrefTitle')}
                  </div>
                  <div className="text-xs text-[#737373] mt-0.5 max-w-md">
                    {t('dashboard.languagePrefDesc')}
                  </div>
                </div>
              </div>
              <div className="w-full sm:w-44">
                <Select
                  value={currentLang}
                  onValueChange={onLanguageChange}
                  disabled={savingLang}
                >
                  <SelectTrigger
                    data-testid="language-preference-select"
                    className="h-10 border-[#E2E2DF] bg-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr" data-testid="language-option-fr">
                      {t('dashboard.languageFr')}
                    </SelectItem>
                    <SelectItem value="nl" data-testid="language-option-nl">
                      {t('dashboard.languageNl')}
                    </SelectItem>
                    <SelectItem value="en" data-testid="language-option-en">
                      {t('dashboard.languageEn')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <p className="text-sm text-[#737373]">
            Modifiez votre nom, téléphone ou email. Le téléphone est utilisé pour recevoir vos codes d'accès par SMS.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[#737373] mb-1">Nom complet</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-10"
                data-testid="profile-name-input"
              />
            </div>
            <div>
              <label className="block text-xs text-[#737373] mb-1">
                Téléphone <span className="text-[#737373]/60">(pour les SMS)</span>
              </label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+32 470 12 34 56"
                className="h-10"
                data-testid="profile-phone-input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-[#737373] mb-1">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isGoogleOnly}
                className="h-10"
                data-testid="profile-email-input"
              />
              {isGoogleOnly && (
                <p className="text-[11px] text-[#737373] mt-1">
                  Compte Google : l'email ne peut pas être modifié ici. Utilisez votre compte Google pour le changer.
                </p>
              )}
              {!isGoogleOnly && (
                <p className="text-[11px] text-[#C77D63] mt-1">
                  ⚠ Changer l'email modifie aussi votre identifiant de connexion. Les factures déjà émises gardent l'ancien email.
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-[#E2E2DF] pt-4">
            <Button
              onClick={saveProfile}
              disabled={savingProfile || !dirty}
              data-testid="profile-save-btn"
              className="bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white"
            >
              {savingProfile ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
            {!isGoogleOnly && (
              <Button
                variant="outline"
                onClick={() => setShowPwd((s) => !s)}
                data-testid="profile-toggle-password-btn"
                className="border-[#E2E2DF]"
              >
                {showPwd ? 'Annuler' : 'Changer le mot de passe'}
              </Button>
            )}
          </div>

          {showPwd && !isGoogleOnly && (
            <div className="bg-[#F9F9F7] border border-[#E2E2DF] rounded-md p-4 space-y-3">
              <h3 className="font-semibold text-sm text-[#1A3C34]">Nouveau mot de passe</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-[#737373] mb-1">Mot de passe actuel</label>
                  <Input
                    type="password"
                    value={currentPwd}
                    onChange={(e) => setCurrentPwd(e.target.value)}
                    autoComplete="current-password"
                    className="h-10"
                    data-testid="pwd-current-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#737373] mb-1">Nouveau (≥ 8 car.)</label>
                  <Input
                    type="password"
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    autoComplete="new-password"
                    className="h-10"
                    data-testid="pwd-new-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#737373] mb-1">Confirmation</label>
                  <Input
                    type="password"
                    value={newPwd2}
                    onChange={(e) => setNewPwd2(e.target.value)}
                    autoComplete="new-password"
                    className="h-10"
                    data-testid="pwd-confirm-input"
                  />
                </div>
              </div>
              <Button
                onClick={savePassword}
                disabled={savingPwd}
                data-testid="pwd-save-btn"
                className="bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white"
              >
                {savingPwd ? 'Modification…' : 'Modifier le mot de passe'}
              </Button>
            </div>
          )}

          {/* RGPD — Account deletion (right to be forgotten) */}
          <div className="border-t border-[#E2E2DF] pt-4 mt-4">
            <h3 className="font-semibold text-sm text-[#1A3C34] mb-1">Supprimer mon compte</h3>
            <p className="text-xs text-[#737373] leading-relaxed mb-3">
              Conformément au RGPD (art. 17), vous pouvez demander la suppression de votre compte à tout moment.
              Vos réservations et factures seront <strong>anonymisées</strong> (conservation légale 7 ans en Belgique pour la TVA),
              mais ne contiendront plus aucune donnée personnelle vous identifiant. <strong>Cette action est irréversible.</strong>
            </p>
            {!showDelete ? (
              <Button
                variant="outline"
                onClick={() => setShowDelete(true)}
                data-testid="account-delete-show-btn"
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer mon compte
              </Button>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 space-y-3" data-testid="account-delete-form">
                {!isGoogleOnly && (
                  <div>
                    <label className="block text-xs text-[#737373] mb-1">Confirmez votre mot de passe</label>
                    <Input
                      type="password"
                      value={deletePwd}
                      onChange={(e) => setDeletePwd(e.target.value)}
                      autoComplete="current-password"
                      className="h-10 max-w-sm"
                      data-testid="account-delete-pwd"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-xs text-[#737373] mb-1">
                    Tapez <strong className="text-red-700">SUPPRIMER</strong> pour confirmer
                  </label>
                  <Input
                    value={deleteConfirm}
                    onChange={(e) => setDeleteConfirm(e.target.value)}
                    placeholder="SUPPRIMER"
                    className="h-10 max-w-sm uppercase font-mono"
                    data-testid="account-delete-confirm"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={deleteAccount}
                    disabled={deleting || (deleteConfirm.trim().toUpperCase() !== 'SUPPRIMER')}
                    data-testid="account-delete-confirm-btn"
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {deleting ? 'Suppression…' : 'Confirmer la suppression définitive'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setShowDelete(false); setDeletePwd(''); setDeleteConfirm(''); }}
                    disabled={deleting}
                    data-testid="account-delete-cancel-btn"
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
    </div>
  );
}


// ============================================================
// BILLING PROFILE SECTION — merged into "Mes données".
// 3-way customer type:
//   • Particulier          → private (no legal fields)
//   • Pro assujetti TVA    → pro_vat (VAT + VIES + Peppol opt-in)
//   • Pro non assujetti    → pro_non_vat (BCE 10 chiffres, ASBL / franchisé)
// ============================================================
function BillingProfileSection({ token, getHeaders, user, setUser }) {
  const { t } = useTranslation();
  const [customerType, setCustomerType] = useState(user?.customer_type || 'private');
  const [company, setCompany] = useState(user?.company || '');
  const [vat, setVat] = useState(user?.company_vat || '');
  const [bce, setBce] = useState(user?.company_bce || '');
  const [address, setAddress] = useState(user?.address || '');
  const [peppolEnabled, setPeppolEnabled] = useState(Boolean(user?.peppol_enabled));
  const [saving, setSaving] = useState(false);
  const [viesLoading, setViesLoading] = useState(false);
  const [viesResult, setViesResult] = useState(null);

  // Sync when user prop refreshes (e.g. after /auth/me re-fetch)
  useEffect(() => {
    setCustomerType(user?.customer_type || 'private');
    setCompany(user?.company || '');
    setVat(user?.company_vat || '');
    setBce(user?.company_bce || '');
    setAddress(user?.address || '');
    setPeppolEnabled(Boolean(user?.peppol_enabled));
  }, [user?.customer_type, user?.company, user?.company_vat, user?.company_bce, user?.address, user?.peppol_enabled]);

  const isPrivate = customerType === 'private';
  const isProVat = customerType === 'pro_vat';
  const isProNonVat = customerType === 'pro_non_vat';
  const canEnablePeppol = isProVat && Boolean((vat || '').trim());

  const lookupViaVies = async () => {
    if (!vat.trim()) {
      toast.error("Saisissez d'abord un n° TVA");
      return;
    }
    setViesLoading(true);
    setViesResult(null);
    try {
      const res = await axios.get(`${API}/lookup/vat`, {
        params: { vat: vat.trim() },
        headers: getHeaders(),
        withCredentials: true,
      });
      if (!res.data?.valid) {
        setViesResult({ valid: false });
        toast.error('N° TVA invalide selon VIES');
        return;
      }
      if (res.data.name) setCompany(res.data.name);
      const parts = [res.data.address, res.data.postal_code, res.data.city]
        .map((s) => (s || '').trim())
        .filter(Boolean);
      if (parts.length) setAddress(parts.join(', '));
      setViesResult(res.data);
      toast.success('Données officielles récupérées via VIES');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'VIES indisponible, réessayez';
      toast.error(msg);
    } finally {
      setViesLoading(false);
    }
  };

  const save = async () => {
    // Client-side sanity: private drops all legal fields (backend enforces too).
    if (isProVat && !vat.trim()) {
      toast.error('Numéro de TVA requis pour un pro assujetti');
      return;
    }
    if (isProNonVat && !bce.trim()) {
      toast.error("Numéro d'entreprise (BCE) requis pour un pro non assujetti");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer_type: customerType,
        company: isPrivate ? '' : company,
        company_vat: isProVat ? vat : '',
        company_bce: isProNonVat ? bce : '',
        address: isPrivate ? '' : address,
        peppol_enabled: canEnablePeppol && peppolEnabled,
      };
      const res = await axios.put(
        `${API}/auth/billing-profile`,
        payload,
        { headers: getHeaders(), withCredentials: true },
      );
      if (setUser && res.data) {
        setUser((u) => ({ ...(u || {}), ...res.data }));
      }
      toast.success(t('billing.saved', { defaultValue: 'Données de facturation enregistrées' }));
    } catch (e) {
      const msg = e?.response?.data?.detail || t('billing.saveError', { defaultValue: "Erreur lors de l'enregistrement" });
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-10" data-testid="billing-profile-section">
      <div className="flex items-center gap-2 mb-4">
        <Building2 className="w-5 h-5 text-[#1A3C34]" />
        <h2 className="text-xl font-semibold text-[#1A3C34]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('billing.title', { defaultValue: 'Facturation' })}
        </h2>
      </div>

      <div className="bg-white border border-[#E2E2DF] rounded-lg p-5 space-y-5">
        <p className="text-sm text-[#737373]">
          Choisissez votre statut fiscal. Ces informations apparaîtront sur toutes vos prochaines factures.
        </p>

        {/* 3-way customer type radio group */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="customer-type-group">
          <label
            className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition ${
              isPrivate ? 'border-[#1A3C34] bg-[#F0EFEA]' : 'border-[#E2E2DF] hover:bg-[#F9F9F7]'
            }`}
            data-testid="customer-type-private-label"
          >
            <input
              type="radio"
              name="customer-type"
              value="private"
              checked={isPrivate}
              onChange={() => setCustomerType('private')}
              className="mt-1 w-4 h-4 accent-[#1A3C34]"
              data-testid="customer-type-private"
            />
            <div>
              <div className="font-medium text-sm text-[#1A3C34]">Particulier</div>
              <div className="text-[11px] text-[#737373] mt-0.5">
                Facture à mon nom, sans TVA ni société.
              </div>
            </div>
          </label>

          <label
            className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition ${
              isProVat ? 'border-[#1A3C34] bg-[#F0EFEA]' : 'border-[#E2E2DF] hover:bg-[#F9F9F7]'
            }`}
            data-testid="customer-type-pro-vat-label"
          >
            <input
              type="radio"
              name="customer-type"
              value="pro_vat"
              checked={isProVat}
              onChange={() => setCustomerType('pro_vat')}
              className="mt-1 w-4 h-4 accent-[#1A3C34]"
              data-testid="customer-type-pro-vat"
            />
            <div>
              <div className="font-medium text-sm text-[#1A3C34]">Pro (assujetti TVA)</div>
              <div className="text-[11px] text-[#737373] mt-0.5">
                Je déduis la TVA. Facture avec n° TVA (ex : BE0477795373).
              </div>
            </div>
          </label>

          <label
            className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition ${
              isProNonVat ? 'border-[#1A3C34] bg-[#F0EFEA]' : 'border-[#E2E2DF] hover:bg-[#F9F9F7]'
            }`}
            data-testid="customer-type-pro-novat-label"
          >
            <input
              type="radio"
              name="customer-type"
              value="pro_non_vat"
              checked={isProNonVat}
              onChange={() => setCustomerType('pro_non_vat')}
              className="mt-1 w-4 h-4 accent-[#1A3C34]"
              data-testid="customer-type-pro-novat"
            />
            <div>
              <div className="font-medium text-sm text-[#1A3C34]">Pro (non assujetti)</div>
              <div className="text-[11px] text-[#737373] mt-0.5">
                ASBL, franchisé &lt; 25k€ — pas de TVA, uniquement n° d&apos;entreprise BCE.
              </div>
            </div>
          </label>
        </div>

        {/* Legal fields — hidden for private, tailored for VAT vs BCE */}
        {!isPrivate && (
          <div className="space-y-4 pt-2 border-t border-[#E2E2DF]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#737373] mb-1">
                  {isProNonVat ? "Nom de l'organisation / ASBL" : t('billing.companyName', { defaultValue: 'Nom de la société' })}
                </label>
                <Input
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder={isProNonVat ? 'Mon ASBL' : 'ACME SRL'}
                  className="h-10 border-[#E2E2DF] rounded-md"
                  data-testid="billing-company-input"
                />
              </div>

              {isProVat && (
                <div>
                  <label className="block text-xs text-[#737373] mb-1">
                    {t('billing.vatNumber', { defaultValue: 'Numéro de TVA' })}
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={vat}
                      onChange={(e) => setVat(e.target.value.toUpperCase())}
                      placeholder="BE0477795373"
                      className="h-10 border-[#E2E2DF] rounded-md font-mono flex-1"
                      data-testid="billing-vat-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={lookupViaVies}
                      disabled={viesLoading || !vat.trim()}
                      className="h-10 px-3 border-[#C77D63]/40 text-[#C77D63] hover:bg-[#C77D63]/10 whitespace-nowrap"
                      data-testid="vies-lookup-btn"
                      title="Vérifier et pré-remplir depuis VIES (base UE officielle)"
                    >
                      {viesLoading ? '…' : 'Vérifier'}
                    </Button>
                  </div>
                  <p className="text-[11px] text-[#737373] mt-1">
                    {t('billing.vatHint', { defaultValue: 'Format : code pays + chiffres (BE, FR, NL, LU…)' })}
                    {viesResult?.valid && (
                      <span className="text-green-700 font-medium ml-1">✓ TVA valide</span>
                    )}
                  </p>
                </div>
              )}

              {isProNonVat && (
                <div>
                  <label className="block text-xs text-[#737373] mb-1">
                    Numéro d&apos;entreprise (BCE)
                  </label>
                  <Input
                    value={bce}
                    onChange={(e) => setBce(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="0477795373"
                    maxLength={10}
                    className="h-10 border-[#E2E2DF] rounded-md font-mono"
                    data-testid="billing-bce-input"
                  />
                  <p className="text-[11px] text-[#737373] mt-1">
                    10 chiffres, sans le préfixe « BE ». Consultez votre statut sur{' '}
                    <a
                      href="https://kbopub.economie.fgov.be/kbopub/zoeknummerform.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#C77D63] hover:underline"
                    >
                      la Banque-Carrefour des Entreprises
                    </a>.
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs text-[#737373] mb-1">
                {t('billing.address', { defaultValue: 'Adresse de facturation' })}
              </label>
              <Input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Rue, n°, code postal, ville, pays"
                className="h-10 border-[#E2E2DF] rounded-md"
                data-testid="billing-address-input"
              />
            </div>

            {/* Peppol opt-in — only meaningful for VAT-registered pros */}
            {isProVat && (
              <label
                className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition ${
                  canEnablePeppol ? 'border-[#E2E2DF] hover:bg-[#F9F9F7]' : 'border-[#E2E2DF] opacity-60 cursor-not-allowed'
                }`}
                data-testid="peppol-optin-label"
              >
                <input
                  type="checkbox"
                  checked={peppolEnabled && canEnablePeppol}
                  onChange={(e) => setPeppolEnabled(e.target.checked)}
                  disabled={!canEnablePeppol}
                  className="mt-1 w-4 h-4 accent-[#1A3C34]"
                  data-testid="peppol-optin-checkbox"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Send className="w-4 h-4 text-[#1A3C34]" />
                    <span className="font-medium text-[#1A3C34]">
                      {t('billing.peppolTitle', { defaultValue: 'Recevoir mes factures via Peppol (e-invoicing)' })}
                    </span>
                  </div>
                  <p className="text-xs text-[#737373] mt-1">
                    {canEnablePeppol
                      ? t('billing.peppolDesc', {
                          defaultValue: "Réseau européen d'échange de factures électroniques (UBL 2.1). Si votre logiciel comptable est connecté à Peppol, vous recevrez vos factures directement dedans, sans email. Sinon, laissez décoché : vous continuerez à recevoir un PDF par email.",
                        })
                      : t('billing.peppolNoVat', {
                          defaultValue: "Indiquez d'abord un numéro de TVA pour activer cette option.",
                        })}
                  </p>
                  {peppolEnabled && canEnablePeppol && (
                    <p className="text-[11px] text-[#A0826D] mt-1">
                      ⓘ {t('billing.peppolPending', {
                        defaultValue: "Votre préférence est enregistrée. L'envoi via Peppol sera activé dès la mise en service de notre Access Point (prochainement).",
                      })}
                    </p>
                  )}
                </div>
              </label>
            )}
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90"
            data-testid="billing-save-btn"
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            {saving ? t('billing.saving', { defaultValue: 'Enregistrement…' }) : t('billing.save', { defaultValue: 'Enregistrer' })}
          </Button>
        </div>
      </div>
    </div>
  );
}

